/**
 * The Patients directory's filter and sort model — pure, so every server
 * query it produces is testable. Everything here compiles to the backend's
 * own allow-list (cms-backend patients.service PATIENT_ALLOW_LIST) and runs
 * over the WHOLE clinic-scoped set; nothing is ever applied to one fetched
 * page in the browser.
 *
 *  - file number    → recordNumber eq
 *  - age / range    → dateOfBirth between|gte|lte, the window derived from
 *                     the product's age rule (whole completed years, UTC)
 *  - caregiver      → caregiverName contains (needs the contacts key)
 *  - status         → registeredAt isnull (true = Guest, false = Patient)
 *  - mobile         → contactMobile | homePhone | altPhone contains (one per request), digits folded like the search
 *  - autism         → autismIdentified eq true (ZOY-666; filterable, not sortable)
 *  - sorting        → lastName/firstName, recordNumber, dateOfBirth,
 *                     registeredAt — the sortable fields, no others
 */

import type { QueryFilter, QuerySort } from '@/core/api/querySpec';
import { mobileContainsTerm } from './lifecycle';

export type StatusFilter = 'all' | 'patient' | 'guest';
/** 'all' shows everyone; 'yes' narrows to patients the clinic identifies as autistic. */
export type AutismFilter = 'all' | 'yes';

/** ZOY-705: the record's own numbers the Contact filter and search ladder through (caregiver numbers are another person's). */
export const PHONE_FIELDS = ['contactMobile', 'homePhone', 'altPhone'] as const;
export type PhoneField = (typeof PHONE_FIELDS)[number];

export interface DirectoryFilters {
  record: number | null;
  ageMin: number | null;
  ageMax: number | null;
  caregiver: string;
  status: StatusFilter;
  /** Any of the record's own numbers — the list tries contact, home and additional (one field per request). */
  phone: string;
  autism: AutismFilter;
}

export const EMPTY_FILTERS: DirectoryFilters = {
  record: null,
  ageMin: null,
  ageMax: null,
  caregiver: '',
  status: 'all',
  phone: '',
  autism: 'all',
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** UTC date-only arithmetic, the same frame ageFromDob computes in. */
function shiftYears(iso: string, years: number, days = 0): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() - years);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Age N (whole completed years) on `today` ⇔ dob ∈ [today−(N+1)y+1d, today−Ny].
 * So age ≥ min bounds the dob from ABOVE, age ≤ max from BELOW.
 */
export function dobWindowForAges(
  min: number | null,
  max: number | null,
  today: string,
): { gte?: string; lte?: string } {
  const w: { gte?: string; lte?: string } = {};
  if (min !== null) w.lte = shiftYears(today, min);
  if (max !== null) w.gte = shiftYears(today, max + 1, 1);
  return w;
}

/** Whole non-negative years from free input (Persian digits welcome); null when not an age. */
export function parseAge(raw: string): number | null {
  const ascii = raw.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).trim();
  if (!/^\d{1,3}$/.test(ascii)) return null;
  const n = Number(ascii);
  return n >= 0 && n <= 150 ? n : null;
}

export function isFilterActive(f: DirectoryFilters): boolean {
  return (
    f.record !== null ||
    f.ageMin !== null ||
    f.ageMax !== null ||
    f.caregiver.trim() !== '' ||
    f.status !== 'all' ||
    f.phone.trim() !== '' ||
    f.autism !== 'all'
  );
}

/** The server query these filters mean — ANDed, whole-set, no client-side leftovers. */
export function compileDirectoryFilters(
  f: DirectoryFilters,
  canContacts: boolean,
  today: string,
  /** ZOY-705: which of the record's numbers this request asks `phone` of (the caller ladders through them). */
  phoneField: PhoneField = 'contactMobile',
): QueryFilter[] {
  const out: QueryFilter[] = [];
  if (f.record !== null) out.push({ field: 'recordNumber', op: 'eq', value: f.record });
  const w = dobWindowForAges(f.ageMin, f.ageMax, today);
  if (w.gte && w.lte) out.push({ field: 'dateOfBirth', op: 'between', value: [w.gte, w.lte] });
  else if (w.gte) out.push({ field: 'dateOfBirth', op: 'gte', value: w.gte });
  else if (w.lte) out.push({ field: 'dateOfBirth', op: 'lte', value: w.lte });
  // caregiverName is queryable only under the contacts key — without it the
  // filter is not offered, and it must never silently become a no-op.
  if (canContacts && f.caregiver.trim() !== '')
    out.push({ field: 'caregiverName', op: 'contains', value: f.caregiver.trim() });
  if (f.status === 'guest') out.push({ field: 'registeredAt', op: 'isnull', value: true });
  if (f.status === 'patient') out.push({ field: 'registeredAt', op: 'isnull', value: false });
  if (f.phone.trim() !== '')
    out.push({ field: phoneField, op: 'contains', value: mobileContainsTerm(f.phone) });
  if (f.autism === 'yes') out.push({ field: 'autismIdentified', op: 'eq', value: true });
  return out;
}

export type SortKey = 'patient' | 'record' | 'age';
export interface DirectorySort {
  key: SortKey;
  dir: 'asc' | 'desc';
}
export const DEFAULT_SORT: DirectorySort = { key: 'patient', dir: 'asc' };

/**
 * Age ascending means youngest first, which is the LATEST date of birth
 * first — so the dob direction is the inverse of the age direction.
 */
export function sortSpec(s: DirectorySort): QuerySort[] {
  switch (s.key) {
    case 'patient':
      return [
        { field: 'lastName', dir: s.dir },
        { field: 'firstName', dir: s.dir },
      ];
    case 'record':
      return [{ field: 'recordNumber', dir: s.dir }];
    case 'age':
      return [{ field: 'dateOfBirth', dir: s.dir === 'asc' ? 'desc' : 'asc' }];
  }
}

export function toggleSort(current: DirectorySort, key: SortKey): DirectorySort {
  if (current.key !== key) return { key, dir: 'asc' };
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

export type ChipId = 'record' | 'age' | 'caregiver' | 'status' | 'phone' | 'autism';

/** The active filters as removable chips, one per filter, in column order. */
export function activeChips(f: DirectoryFilters): ChipId[] {
  const out: ChipId[] = [];
  if (f.record !== null) out.push('record');
  if (f.ageMin !== null || f.ageMax !== null) out.push('age');
  if (f.caregiver.trim() !== '') out.push('caregiver');
  if (f.status !== 'all') out.push('status');
  if (f.phone.trim() !== '') out.push('phone');
  if (f.autism !== 'all') out.push('autism');
  return out;
}

export function withoutChip(f: DirectoryFilters, id: ChipId): DirectoryFilters {
  switch (id) {
    case 'record':
      return { ...f, record: null };
    case 'age':
      return { ...f, ageMin: null, ageMax: null };
    case 'caregiver':
      return { ...f, caregiver: '' };
    case 'status':
      return { ...f, status: 'all' };
    case 'phone':
      return { ...f, phone: '' };
    case 'autism':
      return { ...f, autism: 'all' };
  }
}
